import React from "react";
import { Dialog, KeyValueList, ValueOrPlaceholder } from "@sscs/ui";
import { NotificationsActiveIcon as MUINotificationsActiveIcon } from "@sscs/ui/icons";
import { FileSoftwareInfo, VersionSoftwareInfo } from "Types";
import styles from "./softwareInfo.module.scss";

const SOFTWARE_INFO_OVERVIEW_FIELDS: Array<keyof VersionSoftwareInfo> = [
  "product",
  "version",
  "predecessor_id",
  "publisher",
  "platform",
  "architecture",
  "category",
  "license",
  "release_date",
  "force",
  "replace",
] as const;

const SOFTWARE_INFO_OVERVIEW_LABELS: {
  [key in keyof VersionSoftwareInfo]?: string;
} = {
  product: "Product",
  version: "Version",
  predecessor_id: "Previous version",
  publisher: "Publisher",
  category: "Category",
  license: "License",
  platform: "Platform",
  architecture: "Architecture",
  release_date: "Release date",
  force: "Delete oldest",
  replace: "Replace existing",
} as const;

const VERSION_SPECIFIC_FIELDS: Array<keyof VersionSoftwareInfo> = [
  "predecessor_id",
  "is_released",
  "release_date",
  "force",
  "replace",
];

type SoftwareInfoOverviewProps = (
  | {
      softwareInfo: VersionSoftwareInfo;
      isVersion: true;
      predecessorVersion: string | undefined;
      showForce: boolean;
      showReplace: boolean;
    }
  | {
      softwareInfo: FileSoftwareInfo;
      isVersion: false;
      predecessorVersion?: never;
      showForce?: never;
      showReplace?: never;
    }
) & {
  /** `null` omits the fields' heading — for recaps whose context already says it. */
  title?: string | null;
  /**
   * Recap blocks shown above the software-info fields in the SAME KeyValueList,
   * because two sibling lists cannot share a key column (dialog-inventory rule
   * 2). `title` then labels the fields as a sub-heading.
   */
  leadingList?: Array<Record<string, React.ReactNode> | string>;
};

const SoftwareInfoOverview = ({
  softwareInfo,
  isVersion,
  predecessorVersion,
  showForce = false,
  showReplace = false,
  title = "Software info",
  leadingList,
}: SoftwareInfoOverviewProps): React.JSX.Element => {
  const fields = SOFTWARE_INFO_OVERVIEW_FIELDS.filter((item) => {
    if (isVersion) {
      if (item === "force") {
        return showForce;
      } else if (item === "replace") {
        return showReplace;
      } else {
        return true;
      }
    } else {
      return !VERSION_SPECIFIC_FIELDS.includes(item);
    }
  });

  const fieldList = Object.fromEntries(
    fields.map((fieldName) => {
      // cast is safe because we filter out version specific fields if overview is for a file stream file
      let label = (softwareInfo as VersionSoftwareInfo)[fieldName];

      if (fieldName === "predecessor_id") {
        label = predecessorVersion ?? null;
      }

      if (typeof label === "boolean") {
        label = label ? "Yes" : "No";
      }

      return [
        SOFTWARE_INFO_OVERVIEW_LABELS[fieldName],
        <ValueOrPlaceholder key={fieldName} value={label} />,
      ];
    }),
  );

  const list = [...(leadingList ?? []), ...(title ? [title] : []), fieldList];

  if (!softwareInfo.notify_on_completion) {
    return <KeyValueList list={list} />;
  }

  return (
    <Dialog.Section>
      <Dialog.Section.Description>
        <span className={styles["notify-on-completion"]}>
          <MUINotificationsActiveIcon /> You will be notified by email when
          analysis is complete
        </span>
      </Dialog.Section.Description>
      <Dialog.Section.Content>
        <KeyValueList list={list} />
      </Dialog.Section.Content>
    </Dialog.Section>
  );
};

export default SoftwareInfoOverview;
